import {
    Chart as ChartJS,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { Button, Card, Col, Container, Row, Spinner } from "react-bootstrap";
import type { ChartOptions } from "chart.js";
import { Form } from "react-bootstrap";
import { useEffect, useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { fetchDailyAttendances, fetchDesignationsList, fetchPortAvailableCount, fetchPortsList } from "./services/api";
import { Designation, Port } from "./types";
import moment from "moment";


ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

export default function ManpowerDashboard() {
    const getLocalISODate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const getDaysAgoISODate = (daysAgo: number) => {
        const d = new Date();
        d.setDate(d.getDate() - daysAgo);
        return getLocalISODate(d);
    };

    const [fromDate, setFromDate] = useState(() => getDaysAgoISODate(1));
    const [toDate, setToDate] = useState(() => getDaysAgoISODate(1));
    const [ports, setPorts] = useState<Port[]>([]);
    const [designations, setDesignations] = useState<Designation[]>([]);
    const [location, setLocation] = useState("ALL");
    const [filterDesignation, setFilterDesignation] = useState('');
    const [portsAvailableCount, setPortsAvailableCount] = useState<any[]>([]);
    const [totalPortAgreedManpower, setTotalPortAgreedManpower] = useState(0);
    const [totalAvailableManpower, setTotalAvailableManpower] = useState(0);
    const [overallAttendance, setOverallAttendance] = useState(0);
    const [filterLoading, setFilterLoading] = useState(false);
    const [portAttendances, setPortAttendances] = useState<any[]>([]);
    const [totalRegularAttendances, setTotalRegularAttendances] = useState(0);
    const [totalAgreedManpower, setTotalAgreedManpower] = useState(0);
    const [portsLoaded, setPortsLoaded] = useState(false);
    const [designationsLoaded, setDesignationsLoaded] = useState(false);
    const [heavyDataLoading, setHeavyDataLoading] = useState(true);

    /* Load ports and designations first (faster) - no blocking */
    useEffect(() => {
        fetchPortsList()
            .then(response => {
                const list = Array.isArray(response.data) ? response.data : [];
                setPorts(list);
            })
            .catch(() => setPorts([]))
            .finally(() => setPortsLoaded(true));
        fetchDesignationsList()
            .then(response => {
                const list = Array.isArray(response.data) ? response.data : [];
                setDesignations(list);
            })
            .catch(() => setDesignations([]))
            .finally(() => setDesignationsLoaded(true));
    }, []);

    /* Load heavy data (port count + daily attendances) - show skeletons until done */
    useEffect(() => {
        setHeavyDataLoading(true);
        // Charts should be based on the last 1 month
        const minFromDate = new Date(new Date().setMonth(new Date().getMonth() - 1, 1)).toISOString().split('T')[0];
        Promise.all([
            fetchPortAvailableCount()
                .then(response => setPortsAvailableCount(response))
                .catch(() => setPortsAvailableCount([])),
            fetchDailyAttendances({ startDate: minFromDate, endDate: toDate, portId: '', designationId: '' })
                .then(response => {
                    type GroupedPort = {
                        portId: number;
                        portName: string;
                        totalAttendance: number;
                    };
                    const data = response.data.filter((item: any) => item.attendance_type === "REGULAR");
                    setTotalRegularAttendances(data.length);
                    const groupedByPort = data.reduce((acc: Record<number, GroupedPort>, item: any) => {
                        const port = item.port;
                        if (!port || typeof port.id !== 'number') return acc;
                        if (!acc[port.id]) {
                            acc[port.id] = {
                                portId: port.id,
                                portName: port.name,
                                totalAttendance: 0,
                            };
                        }
                        acc[port.id].totalAttendance += 1;
                        return acc;
                    }, {} as Record<number, GroupedPort>);
                    const d = Object.values(groupedByPort).map((item: any) => ({
                        port_id: item.portId,
                        port_name: item.portName,
                        attendance_count: item.totalAttendance,
                    }));
                    console.log('setPortAttendances', JSON.stringify(d));
                    setPortAttendances(d);
                })
                .catch(() => setPortAttendances([])),
        ]).finally(() => setHeavyDataLoading(false));
    }, [toDate]);

    useEffect(() => {
        setTotalAgreedManpower(portsAvailableCount.reduce((acc, curr) => acc + curr.available_count, 0) * 90);
        if (location === "ALL") {

            const totalDays = moment(toDate).diff(moment(fromDate), 'days') + 1;
            const totalAgreedManpower = portsAvailableCount.reduce((acc, curr) => acc + curr.available_count, 0) * totalDays;

            setTotalPortAgreedManpower(totalAgreedManpower);
        } else {
            const totalDays = moment(toDate).diff(moment(fromDate), 'days') + 1;
            const totalAgreedManpower = portsAvailableCount.find(port => port.port_id.toString() === location)?.available_count * totalDays;
            setTotalPortAgreedManpower(totalAgreedManpower);
        }
    }, [location, portsAvailableCount, fromDate, toDate]);

    /* -------------------- LEFT CHART -------------------- */
    const attendanceData = useMemo(() => {
        const startOfThreeMonthsAgo = new Date();
        // For expected attendance calculations
        startOfThreeMonthsAgo.setMonth(startOfThreeMonthsAgo.getMonth() - 1, 1);
        const daysInLast3Months = Math.max(1, moment(toDate).diff(moment(startOfThreeMonthsAgo), 'days'));

        const percentageData = portAttendances.map((item: any) => {
            const availableCount = portsAvailableCount.find((p: any) => p.port_id === item.port_id)?.available_count ?? 0;
            const expectedAttendance = availableCount * daysInLast3Months;
            const pct = expectedAttendance > 0 ? (item.attendance_count / expectedAttendance) * 100 : 0;
            return pct;
        });
        return {
            labels: portAttendances.sort((a: any, b: any) => b.attendance_count - a.attendance_count).slice(0, 5).map((item: any) => item.port_name),
            datasets: [
                {
                    data: percentageData.sort((a: any, b: any) => b - a).slice(0, 5),
                    backgroundColor: "lightblue",
                    borderRadius: 6,
                },
            ],
        };
    }, [portAttendances, portsAvailableCount, toDate]);

    const attendanceOptions: ChartOptions<"bar"> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
            x: { grid: { display: false } },
            y: {
                grid: { display: false },
                max: 100,
                ticks: {
                    callback: (value) => `${value}%`,
                },
            },
        },
    };

    /* -------------------- RIGHT CHART -------------------- */
    const manpowerData = {
        labels: [
            "Total Agreed Manpower",
            "Total Available Manpower",
            "Difference",
        ],
        datasets: [
            {
                data: [totalAgreedManpower, totalRegularAttendances, totalAgreedManpower - totalRegularAttendances],
                backgroundColor: ["#b7c6f7", "#bb6bd9", "#ff4d2d"],
                borderRadius: 6,
            },
        ],
    };

    const manpowerOptions: ChartOptions<"bar"> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
            x: { grid: { display: false } },
            y: {
                grid: { display: false },
                ticks: {
                    callback: (value) => `${Number(value) / 1000}K`,
                },
            },
        },
    };

    const handleFilterClick = async () => {
        setFilterLoading(true);
        try {
            let portId = location;
            if (location === 'ALL') {
                portId = '';
            }
            let designationId = filterDesignation;
            if (filterDesignation === '0') {
                designationId = '';
            }
            const result = await fetchDailyAttendances({ startDate: fromDate, endDate: toDate, portId: portId, designationId: designationId });
            const data = result.data.filter((item: any) => item.attendance_type === "REGULAR");
            console.log('data', data.length);
            setTotalAvailableManpower(data.length);
            const overallAttendance = totalPortAgreedManpower > 0
                ? Math.round(((data.length / totalPortAgreedManpower) * 100) * 100) / 100
                : 0;
            setOverallAttendance(overallAttendance);
        } finally {
            setFilterLoading(false);
        }
    }


    return (
        <Container fluid className="p-4 p-md-5">
            {/* Date Filters */}
            <Card className="p-3 h-100 shadow-sm mb-4">
                <Row className="g-2">
                    <Col xs={2} style={{ marginTop: 16 }}>
                        <Form.Control
                            type="date"
                            size="sm"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                        />
                    </Col>
                    <Col xs={2} style={{ marginTop: 16 }}>
                        <Form.Control
                            type="date"
                            size="sm"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                        />
                    </Col>
                    {/* Location Filter */}
                    <Col xs={2}>
                        {!portsLoaded ? (
                            <div className="mt-2"><Skeleton height={32} /></div>
                        ) : (
                            <Form.Select
                                size="sm"
                                className="mt-2"
                                value={location}
                                onChange={(e) => setLocation(e.target.value)}
                            >
                                {ports.map(port => (
                                    <option key={port.id} value={port.id}>
                                        {port.name}
                                    </option>
                                ))}
                                <option value="ALL">All Locations</option>
                            </Form.Select>
                        )}
                    </Col>
                    {/* Designation Filter */}
                    <Col xs={2}>
                        {!designationsLoaded ? (
                            <div className="mt-2"><Skeleton height={32} /></div>
                        ) : (
                            <Form.Select
                                size="sm"
                                className="mt-2"
                                value={filterDesignation}
                                onChange={(e) => setFilterDesignation(e.target.value)}
                            >
                                <option key={0} value={0}>All Designations</option>
                                {designations.map(designation => (
                                    <option key={designation.id} value={designation.id}>
                                        {designation.name}
                                    </option>
                                ))}
                            </Form.Select>
                        )}
                    </Col>
                    <Col xs={2}>
                        <Button variant="primary" onClick={handleFilterClick} disabled={filterLoading}>
                            {filterLoading ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Fetching...
                                </>
                            ) : (
                                "Fetch Data"
                            )}
                        </Button>
                    </Col>
                </Row>
                <Row className="mb-4 mt-4">
                    <Col md={4}>
                        <Card className="p-3 h-100 shadow-sm">
                            <>
                                <div className="fs-3">👥</div>
                                <h6 className="text-muted mt-2">Total Agreed Manpower</h6>
                                <h2 className="fw-bold mt-3" style={{ color: "#6fcf97" }}>
                                    {totalPortAgreedManpower}
                                </h2>
                            </>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card className="p-3 h-100 shadow-sm">
                            <>
                                <div className="fs-3">📊</div>
                                <h6 className="text-muted mt-2">Total Available Manpower</h6>
                                <h2 className="fw-bold mt-3" style={{ color: "#bb6bd9" }}>
                                    {totalAvailableManpower}
                                </h2>
                            </>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card className="p-3 h-100 shadow-sm">
                            <>
                                <div className="fs-3">📈</div>
                                <h6 className="text-muted mt-2">Overall Attendance %</h6>
                                <h2 className="fw-bold mt-3" style={{ color: "#8ea7ff" }}>
                                    {overallAttendance}
                                </h2>
                            </>
                        </Card>
                    </Col>
                    {/* <Col md={3}>
                        <Card className="p-3 h-100 shadow-sm">
                            <div className="fs-3">🏆</div>
                            <h6 className="text-muted mt-2">Top Performing Port</h6>
                            <h2 className="fw-bold mt-3" style={{ color: "#b497f2" }}>
                                {topPerformingPort}
                            </h2>
                        </Card>
                    </Col> */}
                </Row>
            </Card>
            {/* -------------------- TOP METRIC CARDS -------------------- */}


            {/* -------------------- CHARTS -------------------- */}
            <Row>
                <Col md={6}>
                    <Card className="p-3 shadow-sm">
                        <h5 className="mb-3">
                            Top Performing Ports by Attendance % <br />
                            <small className="text-muted">(Last 1 Month)</small>
                        </h5>
                        <div style={{ height: 260 }}>
                            {heavyDataLoading ? (
                                <div className="d-flex align-items-end justify-content-between gap-2" style={{ height: "100%", paddingTop: 40 }}>
                                    {[100, 160, 130, 200, 170].map((h, i) => (
                                        <Skeleton key={i} height={h} width="18%" style={{ borderRadius: 6 }} />
                                    ))}
                                </div>
                            ) : (
                                <Bar data={attendanceData} options={attendanceOptions} />
                            )}
                        </div>
                    </Card>
                </Col>

                <Col md={6}>
                    <Card className="p-3 shadow-sm">
                        <h5 className="mb-3">Manpower status <br />
                            <small className="text-muted">(Last 1 Month)</small>
                        </h5>
                        <div style={{ height: 260 }}>
                            {heavyDataLoading ? (
                                <div className="d-flex align-items-end justify-content-between gap-2" style={{ height: "100%", paddingTop: 40 }}>
                                    {[180, 140, 120].map((h, i) => (
                                        <Skeleton key={i} height={h} width="30%" style={{ borderRadius: 6 }} />
                                    ))}
                                </div>
                            ) : (
                                <Bar data={manpowerData} options={manpowerOptions} />
                            )}
                        </div>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}
